// apps/web/src/lib/compras/pedidos.ts — Fase 13, Plano 17 (COMP-04, D-19, D-20).
// Regras puras da lista de pedidos de compra: paleta de situação, texto de atraso, query string e estado da tela.
// Quem filtra, ordena e deriva a situação é o servidor (`GET /api/v1/compras/pedidos`); nada disso é recalculado aqui.
import type {
  FiltroPrazoPedidoCompra,
  FornecedorLookup,
  PedidoCompraItemView,
  PedidoCompraResumo,
  SituacaoItemPedidoCompra,
  SituacaoPedidoCompra,
} from '@pieltec/contracts';
import { hojeIso } from '../vendas/formatacao';
import { ehUuid } from './uuid';

export type IconeSituacaoPedido = 'CircleDashed' | 'PackageOpen' | 'CheckCircle2' | 'XCircle';

export interface ApresentacaoSituacaoPedido {
  readonly rotulo: string;
  readonly icone: IconeSituacaoPedido;
  readonly classes: string;
}

/** Paleta fechada "Pedido de compra — situação" do UI-SPEC da Fase 13 (cor nunca é o único sinal: ícone + texto). */
export function apresentacaoSituacaoPedido(situacao: SituacaoPedidoCompra): ApresentacaoSituacaoPedido {
  switch (situacao) {
    case 'ABERTO':
      return { rotulo: 'Aberto', icone: 'CircleDashed', classes: 'bg-slate-50 text-slate-700 border-slate-200' };
    case 'PARCIALMENTE_RECEBIDO':
      return { rotulo: 'Parcialmente recebido', icone: 'PackageOpen', classes: 'bg-white text-emerald-700 border-emerald-300' };
    case 'CONCLUIDO':
      return { rotulo: 'Concluído', icone: 'CheckCircle2', classes: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
    case 'CANCELADO':
      return { rotulo: 'Cancelado', icone: 'XCircle', classes: 'bg-slate-100 text-slate-500 border-slate-200' };
  }
}

/** "Atrasado 1 dia" / "Atrasado {N} dias". */
export function textoAtraso(dias: number): string {
  return dias === 1 ? 'Atrasado 1 dia' : `Atrasado ${dias} dias`;
}

/** "{N} pedidos" (1 vira "1 pedido"). */
export function contagemPedidos(total: number): string {
  return total === 1 ? '1 pedido' : `${total} pedidos`;
}

/** Situações no filtro (ordem do ciclo do pedido). */
export const SITUACOES_FILTRO_PEDIDO: readonly SituacaoPedidoCompra[] = ['ABERTO', 'PARCIALMENTE_RECEBIDO', 'CONCLUIDO', 'CANCELADO'];

export type PrazoFiltroPedido = 'TODOS' | FiltroPrazoPedidoCompra;

export const OPCOES_PRAZO_PEDIDO: ReadonlyArray<{ readonly valor: PrazoFiltroPedido; readonly rotulo: string }> = [
  { valor: 'TODOS', rotulo: 'Todos os prazos' },
  { valor: 'ATRASADOS', rotulo: 'Atrasados' },
  { valor: 'PROXIMOS_7', rotulo: 'Próximos 7 dias' },
  { valor: 'PERIODO', rotulo: 'Período…' },
];

export interface FiltrosPedidosCompra {
  busca: string;
  fornecedor: FornecedorLookup | null;
  situacoes: readonly SituacaoPedidoCompra[];
  prazo: PrazoFiltroPedido;
  de: string;
  ate: string;
  incluirConcluidos: boolean;
}

export function filtrosPedidosVazios(): FiltrosPedidosCompra {
  return { busca: '', fornecedor: null, situacoes: [], prazo: 'TODOS', de: '', ate: '', incluirConcluidos: false };
}

/** Há algum filtro além do padrão (busca vazia, sem fornecedor/situação/prazo e sem concluídos)? */
export function filtrosPedidosAtivos(filtros: FiltrosPedidosCompra): boolean {
  return (
    filtros.busca.trim() !== '' ||
    filtros.fornecedor !== null ||
    filtros.situacoes.length > 0 ||
    filtros.prazo !== 'TODOS' ||
    filtros.incluirConcluidos
  );
}

/** Período só vale com as duas datas; enquanto incompleto, o prazo não é enviado (a API exigiria as duas). */
export function prazoEfetivoPedido(filtros: FiltrosPedidosCompra): PrazoFiltroPedido {
  if (filtros.prazo === 'PERIODO' && (filtros.de === '' || filtros.ate === '')) return 'TODOS';
  return filtros.prazo;
}

/** Query string (sem `?`) com só os filtros ativos, na ordem fixa dos contratos; situações vão em ordem canônica. */
export function montarQueryPedidosCompra(filtros: FiltrosPedidosCompra, paginacao: { pagina: number; tamanhoPagina: number }): string {
  const params = new URLSearchParams();
  params.set('pagina', String(paginacao.pagina));
  params.set('tamanhoPagina', String(paginacao.tamanhoPagina));
  const busca = filtros.busca.trim();
  if (busca !== '') params.set('busca', busca);
  if (filtros.fornecedor) params.set('fornecedorId', filtros.fornecedor.id);
  if (filtros.situacoes.length > 0) {
    params.set('situacao', SITUACOES_FILTRO_PEDIDO.filter((situacao) => filtros.situacoes.includes(situacao)).join(','));
  }
  const prazo = prazoEfetivoPedido(filtros);
  if (prazo !== 'TODOS') {
    params.set('prazo', prazo);
    if (prazo === 'PERIODO') {
      params.set('de', filtros.de);
      params.set('ate', filtros.ate);
    }
  }
  if (filtros.incluirConcluidos) params.set('incluirConcluidos', 'true');
  return params.toString();
}

export type EstadoListaPedidos = 'erro' | 'carregando' | 'vazio' | 'sem-resultado' | 'lista';

/** Qual bloco a tela mostra. Erro vence tudo; o skeleton só aparece enquanto ainda não há dados. */
export function estadoDaListaPedidos(entrada: {
  readonly erro: boolean;
  readonly carregando: boolean;
  readonly temDados: boolean;
  readonly total: number;
  readonly comFiltro: boolean;
}): EstadoListaPedidos {
  if (entrada.erro) return 'erro';
  if (entrada.carregando && !entrada.temDados) return 'carregando';
  if (entrada.total === 0) return entrada.comFiltro ? 'sem-resultado' : 'vazio';
  return 'lista';
}

const DIAS_PRAZO_PERTO = 2;

function diasEntre(deIso: string, ateIso: string): number {
  const [a1, m1, d1] = deIso.split('-').map(Number);
  const [a2, m2, d2] = ateIso.split('-').map(Number);
  return Math.round((Date.UTC(a2 ?? 0, (m2 ?? 1) - 1, d2 ?? 1) - Date.UTC(a1 ?? 0, (m1 ?? 1) - 1, d1 ?? 1)) / 86_400_000);
}

/**
 * Urgência do prazo do pedido para a faixa lateral (reuso de faixaUrgencia/iconeUrgencia): atrasado vem do servidor
 * (`piorAtrasoDias`); "perto" é o prazo geral a até 2 dias de hoje. Pedido encerrado ou sem saldo é sempre 'ok'.
 */
export function prazoDoPedido(pedido: PedidoCompraResumo, hoje: string = hojeIso()): 'ok' | 'perto' | 'estourado' {
  if (pedido.situacao === 'CONCLUIDO' || pedido.situacao === 'CANCELADO') return 'ok';
  if (pedido.piorAtrasoDias !== null && pedido.piorAtrasoDias > 0) return 'estourado';
  if (pedido.itensComSaldo <= 0 || !pedido.prazoGeral) return 'ok';
  const dias = diasEntre(hoje, pedido.prazoGeral.slice(0, 10));
  if (dias < 0) return 'estourado';
  return dias <= DIAS_PRAZO_PERTO ? 'perto' : 'ok';
}

// ---------------------------------------------------------------------------------------------------------------------
// Filtros na URL (Task 3): recarregar a página ou compartilhar o link preserva o recorte. Só o que difere do padrão vai.
// O fornecedor leva id + nome + CNPJ para o combobox mostrar o nome sem nova consulta.
// ---------------------------------------------------------------------------------------------------------------------

const DATA_ISO = /^\d{4}-\d{2}-\d{2}$/;
const PRAZOS_VALIDOS: readonly PrazoFiltroPedido[] = ['TODOS', 'ATRASADOS', 'PROXIMOS_7', 'PERIODO'];

/** Lê os filtros de uma query string (com ou sem `?`). Valor inválido volta ao padrão, nunca quebra a tela. */
export function lerFiltrosPedidos(search: string): FiltrosPedidosCompra {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const filtros = filtrosPedidosVazios();
  filtros.busca = (params.get('busca') ?? '').trim();

  const fornecedorId = params.get('fornecedorId');
  if (ehUuid(fornecedorId)) {
    filtros.fornecedor = {
      id: fornecedorId,
      cnpj: params.get('fornecedorCnpj') ?? '',
      razaoSocial: params.get('fornecedorNome') ?? '',
      situacao: 'ATIVO',
    };
  }

  const situacoes = (params.get('situacao') ?? '').split(',').map((parte) => parte.trim());
  filtros.situacoes = SITUACOES_FILTRO_PEDIDO.filter((situacao) => situacoes.includes(situacao));

  const prazo = params.get('prazo');
  if (prazo && (PRAZOS_VALIDOS as readonly string[]).includes(prazo)) filtros.prazo = prazo as PrazoFiltroPedido;

  const de = params.get('de') ?? '';
  const ate = params.get('ate') ?? '';
  if (DATA_ISO.test(de)) filtros.de = de;
  if (DATA_ISO.test(ate)) filtros.ate = ate;

  filtros.incluirConcluidos = params.get('concluidos') === '1';
  return filtros;
}

/** Query string (sem `?`) só com o que difere do padrão; vazia quando nenhum filtro está ativo. */
export function escreverFiltrosPedidos(filtros: FiltrosPedidosCompra): string {
  const params = new URLSearchParams();
  const busca = filtros.busca.trim();
  if (busca !== '') params.set('busca', busca);
  if (filtros.fornecedor) {
    params.set('fornecedorId', filtros.fornecedor.id);
    if (filtros.fornecedor.razaoSocial) params.set('fornecedorNome', filtros.fornecedor.razaoSocial);
    if (filtros.fornecedor.cnpj) params.set('fornecedorCnpj', filtros.fornecedor.cnpj);
  }
  if (filtros.situacoes.length > 0) {
    params.set('situacao', SITUACOES_FILTRO_PEDIDO.filter((situacao) => filtros.situacoes.includes(situacao)).join(','));
  }
  if (filtros.prazo !== 'TODOS') {
    params.set('prazo', filtros.prazo);
    if (filtros.prazo === 'PERIODO') {
      if (filtros.de !== '') params.set('de', filtros.de);
      if (filtros.ate !== '') params.set('ate', filtros.ate);
    }
  }
  if (filtros.incluirConcluidos) params.set('concluidos', '1');
  return params.toString();
}

// ---------------------------------------------------------------------------------------------------------------------
// Detalhe do pedido (Plano 13-19, COMP-04, D-16, D-19): apresentação dos itens. Saldo, recebido, excedente, situação e
// atraso vêm do servidor; aqui só se decide o que mostrar (nada é recalculado a partir de quantidades digitadas).
// ---------------------------------------------------------------------------------------------------------------------

export type IconeSituacaoItem = 'CircleDashed' | 'PackageOpen' | 'CheckCircle2' | 'Archive' | 'XCircle';

export interface ApresentacaoSituacaoItem {
  readonly rotulo: string;
  readonly icone: IconeSituacaoItem;
  readonly classes: string;
}

/** Situação do item: mesma paleta fechada do pedido; "Saldo encerrado" (Archive) e "Cancelado" ficam em tom apagado. */
export function apresentacaoSituacaoItem(situacao: SituacaoItemPedidoCompra): ApresentacaoSituacaoItem {
  switch (situacao) {
    case 'ABERTO':
      return { rotulo: 'Aberto', icone: 'CircleDashed', classes: 'bg-slate-50 text-slate-700 border-slate-200' };
    case 'PARCIAL':
      return { rotulo: 'Parcial', icone: 'PackageOpen', classes: 'bg-white text-emerald-700 border-emerald-300' };
    case 'CONCLUIDO':
      return { rotulo: 'Concluído', icone: 'CheckCircle2', classes: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
    case 'ENCERRADO':
      return { rotulo: 'Saldo encerrado', icone: 'Archive', classes: 'bg-slate-100 text-slate-500 border-slate-200' };
    case 'CANCELADO':
      return { rotulo: 'Cancelado', icone: 'XCircle', classes: 'bg-slate-100 text-slate-500 border-slate-200' };
  }
}

/** Item encerrado ou cancelado: linha apagada e sem ações (D-19). */
export function itemEncerrado(item: Pick<PedidoCompraItemView, 'situacao' | 'saldoEncerrado'>): boolean {
  return item.situacao === 'ENCERRADO' || item.situacao === 'CANCELADO' || item.saldoEncerrado;
}

/** Saldo a receber exibido: nunca negativo e 0 quando o item foi encerrado/cancelado (a falta fica no motivo). */
export function saldoExibido(item: Pick<PedidoCompraItemView, 'saldoAReceber' | 'situacao' | 'saldoEncerrado'>): number {
  if (itemEncerrado(item)) return 0;
  return Math.max(0, item.saldoAReceber);
}

/** Urgência do prazo do ITEM para a faixa lateral: atraso vem do servidor; "perto" = prazo a até 2 dias com saldo aberto. */
export function prazoDoItem(
  item: Pick<PedidoCompraItemView, 'situacao' | 'saldoEncerrado' | 'atrasoDias' | 'prazoEntrega'>,
  hoje: string = hojeIso(),
): 'ok' | 'perto' | 'estourado' {
  if (itemEncerrado(item) || item.situacao === 'CONCLUIDO') return 'ok';
  if (item.atrasoDias !== null && item.atrasoDias > 0) return 'estourado';
  if (!item.prazoEntrega) return 'ok';
  const dias = diasEntre(hoje, item.prazoEntrega.slice(0, 10));
  if (dias < 0) return 'estourado';
  return dias <= DIAS_PRAZO_PERTO ? 'perto' : 'ok';
}

/** Abas do detalhe (valor de `&aba=` na URL); valor desconhecido volta para Itens. */
export const ABAS_DETALHE_PEDIDO = ['itens', 'nfs', 'historico'] as const;
export type AbaDetalhePedido = (typeof ABAS_DETALHE_PEDIDO)[number];

export function abaDoPedidoDaUrl(search: string): AbaDetalhePedido {
  const aba = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search).get('aba');
  return (ABAS_DETALHE_PEDIDO as readonly string[]).includes(aba ?? '') ? (aba as AbaDetalhePedido) : 'itens';
}
